/**
 * Image utilities for handling prop images from multiple sources
 * Supports both Wasabi cloud storage URLs and local images
 */

// Default fallback images for different prop categories
const FALLBACK_IMAGES = {
  furniture: '/images/fallbacks/furniture-default.jpg',
  lighting: '/images/fallbacks/lighting-default.jpg',
  decor: '/images/fallbacks/decor-default.jpg',
  electronics: '/images/fallbacks/electronics-default.jpg',
  textiles: '/images/fallbacks/textiles-default.jpg',
  vehicles: '/images/fallbacks/vehicles-default.jpg',
  default: '/images/fallbacks/prop-default.jpg'
};

const EXTERNAL_IMAGE_BASE = 'https://propshare.josh-w-martin.com';

export const extractImageSource = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    // Remote prop-images stored without protocol
    if (trimmed.startsWith('prop-images/')) {
      return `${EXTERNAL_IMAGE_BASE}/${trimmed}`;
    }
    if (trimmed.startsWith('/prop-images/')) {
      return `${EXTERNAL_IMAGE_BASE}${trimmed}`;
    }

    if (trimmed.startsWith('/')) {
      return trimmed;
    }

    const timestamp = Date.now();
    return `/images/props/${trimmed}?v=${timestamp}`;
  }
  if (typeof value === 'object') {
    const keys = ['src', 'url', 'path', 'image', 'image_url', 'main_image_src'];
    for (const key of keys) {
      if (value[key]) {
        return extractImageSource(value[key]);
      }
    }
  }
  return null;
};

/**
 * Get the primary image path for a prop
 * @param {Object} prop - The prop object
 * @param {string} prop.id - Prop ID
 * @param {Array} prop.images - Array of image URLs (Wasabi) or filenames (local)
 * @param {string} prop.category - Prop category for fallback
 * @returns {string} Image path (Wasabi URL or local path)
 */
export const getPropImagePath = (prop) => {
  if (!prop) return FALLBACK_IMAGES.default;

  const candidateSources = [
    prop.main_image_src,
    prop.images?.[0],
    prop.main_image,
    prop.primary_image,
    prop.image,
    prop.thumbnail
  ];

  for (const candidate of candidateSources) {
    const src = extractImageSource(candidate);
    if (src) return src;
  }

  if (prop.id) {
    const timestamp = prop._imageTimestamp || Date.now();
    return `/images/props/${prop.id}_main.jpg?v=${timestamp}`;
  }

  const category = prop.category?.toLowerCase() || 'default';
  return FALLBACK_IMAGES[category] || FALLBACK_IMAGES.default;
};

/**
 * Get all image paths for a prop (for galleries/carousels)
 * @param {Object} prop - The prop object
 * @returns {Array} Array of image paths (Wasabi URLs or local paths)
 */
export const getPropImagePaths = (prop) => {
  if (!prop) return [FALLBACK_IMAGES.default];

  const candidates = [
    ...(Array.isArray(prop.images) ? prop.images : []),
    ...(Array.isArray(prop.galleryImages) ? prop.galleryImages : []),
    ...(Array.isArray(prop.gallery_images) ? prop.gallery_images : []),
    prop.main_image_src,
    prop.main_image,
    prop.primary_image,
    prop.image,
    prop.thumbnail,
  ];

  const normalized = [];
  const seen = new Set();
  candidates.forEach(candidate => {
    const src = extractImageSource(candidate);
    if (src && !seen.has(src)) {
      seen.add(src);
      normalized.push(src);
    }
  });

  if (!normalized.length) {
    if (prop.id) {
      const timestamp = Date.now();
      const commonImages = [
        `/images/props/${prop.id}_main.jpg?v=${timestamp}`,
        `/images/props/${prop.id}_side.jpg?v=${timestamp}`,
        `/images/props/${prop.id}_detail.jpg?v=${timestamp}`
      ];
      return commonImages;
    }
    return [getPropImagePath(prop)];
  }

  return normalized;
};

/**
 * Handle image load errors with fallback
 * @param {Event} event - Image error event
 * @param {string} category - Prop category for fallback
 */
export const handleImageError = (event, category = 'default') => {
  const img = event.target;
  const fallback = FALLBACK_IMAGES[category?.toLowerCase()] || FALLBACK_IMAGES.default;
  
  // Prevent infinite loop if fallback also fails
  if (img.src !== fallback) {
    img.src = fallback;
  }
};

/**
 * Preload an image to check if it exists
 * @param {string} src - Image source URL
 * @returns {Promise<boolean>} True if image loads successfully
 */
export const checkImageExists = (src) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
};
